import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { Palette } from 'Palette';
import { Permissions } from '../../../models/admin/Permission';
import BackendService from '../../../services/admin/BackendService';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading } from '../../styles/styles';
import StyledModal from '../../styles/StyledModal';
import { StyledButton } from '../../styles/StyledButtons';

const EVENT_TYPES = [
  'FINANCIAL_CLOSE',
  'CAPITAL_CALL',
  'REPATRIATION_OF_CAPITAL',
  'FACILITY_REPAYMENT',
  'DISTRIBUTION',
] as const;

const EVENT_TYPE_LABELS: Record<(typeof EVENT_TYPES)[number], string> = {
  FINANCIAL_CLOSE: 'Financial Close',
  CAPITAL_CALL: 'Capital Call',
  REPATRIATION_OF_CAPITAL: 'Repatriation of Capital',
  FACILITY_REPAYMENT: 'Facility Repayment',
  DISTRIBUTION: 'Distribution',
};

function labelForFundEventType(eventType: string): string {
  return (
    EVENT_TYPE_LABELS[eventType as keyof typeof EVENT_TYPE_LABELS] ??
    eventType.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
  );
}

type FundEventRow = {
  externalId: string;
  eventType: string;
  eventDate: string;
  amount: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

interface FundEventsSectionProps {
  fundOpportunityId: string;
  hasPermission: (permissionKey: Permissions) => boolean;
  onShowToast: (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;
}

const backend = new BackendService();

const FundEventsSection: React.FC<FundEventsSectionProps> = ({
  fundOpportunityId,
  hasPermission,
  onShowToast,
}) => {
  const canEdit = hasPermission(Permissions.FundOpportunityCreation);
  const [events, setEvents] = useState<FundEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState<string>('CAPITAL_CALL');
  const [eventDate, setEventDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<FundEventRow | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editType, setEditType] = useState<string>('CAPITAL_CALL');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await backend.listFundEventsForFund(fundOpportunityId);
      setEvents((data.events ?? []) as FundEventRow[]);
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : 'Failed to load fund events', 'error');
    } finally {
      setLoading(false);
    }
  }, [fundOpportunityId, onShowToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!canEdit) return;
    try {
      await backend.createFundEvent({
        fundOpportunityExternalId: fundOpportunityId,
        eventType,
        eventDate,
        amount: amount.trim() === '' ? null : Number(amount),
        notes: notes.trim() === '' ? null : notes,
      });
      setAmount('');
      setNotes('');
      onShowToast('Fund event created', 'success');
      await load();
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : 'Create failed', 'error');
    }
  };

  const handleDelete = async (externalId: string) => {
    if (!canEdit) return;
    if (!window.confirm('Delete this fund event?')) return;
    try {
      await backend.deleteFundEvent(externalId);
      onShowToast('Event deleted', 'success');
      await load();
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    }
  };

  const openEdit = (row: FundEventRow) => {
    setEditing(row);
    setEditType(row.eventType);
    setEditDate(String(row.eventDate).slice(0, 10));
    setEditAmount(row.amount === null || row.amount === undefined ? '' : String(row.amount));
    setEditNotes(row.notes ?? '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing || !canEdit) return;
    try {
      await backend.updateFundEvent(editing.externalId, {
        eventType: editType,
        eventDate: editDate,
        amount: editAmount.trim() === '' ? null : Number(editAmount),
        notes: editNotes.trim() === '' ? null : editNotes,
      });
      setEditOpen(false);
      setEditing(null);
      onShowToast('Event updated', 'success');
      await load();
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : 'Update failed', 'error');
    }
  };

  const whiteFieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: 'white' } };

  return (
    <StyledSection tone="tone1">
      <StyledHeading>Scheduled Fund Events</StyledHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Events appear in the investor app Home upcoming snapshot (30/90 days), with amounts
        apportioned by committed share. Financial close also uses the fund&apos;s close date.
      </Typography>

      {canEdit && (
        <>
          <StyledHeading headerVariant="subheading">Add Event</StyledHeading>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            sx={{ mt: 1, mb: 3 }}
          >
            <TextField
              select
              label="Type"
              size="small"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              sx={{ minWidth: 220, ...whiteFieldSx }}
            >
              {EVENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {EVENT_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              type="date"
              size="small"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={whiteFieldSx}
            />
            <TextField
              label="Amount (optional)"
              size="small"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={whiteFieldSx}
            />
            <TextField
              label="Notes"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ flex: 1, ...whiteFieldSx }}
            />
            <StyledButton noTopSpacing onClick={() => void handleCreate()}>
              Add
            </StyledButton>
          </Stack>
        </>
      )}

      <Box
        sx={{
          overflowX: 'auto',
          backgroundColor: '#FFFFFF',
          border: `1px solid ${Palette.default.border}`,
          borderRadius: '0.75rem',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: Palette.tone2.background }}>
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }}>Notes</TableCell>
              {canEdit && (
                <TableCell sx={{ fontWeight: 'bold', color: Palette.tone2.text }} align="right">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4}>Loading…</TableCell>
              </TableRow>
            )}
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4}>No scheduled events yet.</TableCell>
              </TableRow>
            )}
            {!loading &&
              events.map((ev, index) => (
                <TableRow
                  key={ev.externalId}
                  sx={{
                    backgroundColor: index % 2 === 0 ? Palette.tone1.background : 'white',
                    '&:hover': { backgroundColor: Palette.tone1.activeBg },
                  }}
                >
                  <TableCell sx={{ color: Palette.tone1.text }}>
                    {labelForFundEventType(ev.eventType)}
                  </TableCell>
                  <TableCell sx={{ color: Palette.tone1.text }}>
                    {String(ev.eventDate).slice(0, 10)}
                  </TableCell>
                  <TableCell sx={{ color: Palette.tone1.text }} align="right">
                    {ev.amount ?? '—'}
                  </TableCell>
                  <TableCell sx={{ color: Palette.tone1.text }}>{ev.notes ?? '—'}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="Edit"
                        onClick={() => openEdit(ev)}
                        sx={{ color: Palette.default.buttonBg }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        onClick={() => void handleDelete(ev.externalId)}
                        sx={{ color: Palette.danger.text }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      <StyledModal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Fund Event">
        <Stack spacing={2} sx={{ mt: 1, minWidth: 280 }}>
          <TextField
            select
            label="Type"
            size="small"
            value={editType}
            onChange={(e) => setEditType(e.target.value)}
          >
            {EVENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {EVENT_TYPE_LABELS[t]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Date"
            type="date"
            size="small"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Amount"
            size="small"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
          />
          <TextField
            label="Notes"
            size="small"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={2}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </StyledButton>
            <StyledButton noTopSpacing onClick={() => void saveEdit()}>
              Save
            </StyledButton>
          </Stack>
        </Stack>
      </StyledModal>
    </StyledSection>
  );
};

export default FundEventsSection;
